import { isEmpty } from "lodash";
import React, { useState, useCallback } from "react";
import {
  SideNavStateProvider,
  SideNavV2,
  Popover,
  Avatar,
  Flex,
  Button,
  Icon,
  Divider,
  H6,
  Badge,
} from "orcs-design-system";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";

import useSideNavClickListener from "src/hooks/useSideNavClickListener";
import {
  useWorkspaceTagConfig,
  useGroupTypes,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useFeatureFlags } from "src/contexts/global/FeatureFlagsContext";
import useSideNavItemBuilder from "src/sidebar/useSideNavItemBuilder";
import eventEmitter from "src/services/eventEmitter";
import { usePinnedEntities } from "src/hooks/usePinnedEntities";
import useUserTeams from "src/components/SideNav/useUserTeams";
import { useUserSideNav } from "src/hooks/userPreferences/hooks/useUserSideNav";
import { useAppHeadersHeightCalc } from "src/hooks/useAppHeadersHeightCalc";
import { getGroupTypeBadgeVariant } from "src/util/group";
import { getGroupType } from "src/util/groupType";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import useFilterNavItems from "./hooks/useFilterNavItems";
import TeamTypeBar from "./components/TeamTypeBar";

// Styled components for team type indicators
const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: ${(props) => (props.isExpanded ? "flex-start" : "center")};
  text-align: ${(props) => (props.isExpanded ? "left" : "center")};
  display: flex;
  flex-direction: column;
  gap: ${(props) => themeGet("space.xs")(props)};
`;

const Item = styled.li`
  display: flex;
  width: 100%;
  gap: ${(props) => themeGet("space.xs")(props)};
  justify-content: ${(props) => {
    if (props.isExpanded) {
      return "space-between";
    }
    return "center";
  }};
  align-items: center;
  padding: ${(props) => (props.isExpanded ? "0" : "0")};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};

  &.active {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:hover {
    background-color: ${(props) =>
      props.isExpanded
        ? themeGet("colors.greyLightest")(props)
        : "transparent"};
  }

  &.active:hover {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }
`;

const TeamLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
  text-decoration: none;
  color: ${(props) => themeGet("colors.greyDarkest")(props)};
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  flex: 1;
  width: 100%;
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};

  /* Ensure all child elements are clickable/hoverable */
  > * {
    pointer-events: none;
  }

  &:hover,
  &:focus {
    outline: none;
    color: ${(props) => themeGet("colors.primary")(props)};
  }
`;

const CollapsedItemWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.space.xs};
  border-radius: ${(props) => props.theme.radii[2]};
  transition: ${(props) => props.theme.transition.transitionDefault};
  cursor: pointer;
  gap: 2px;

  &.active {
    background-color: ${(props) => props.theme.colors.primaryLightest};
  }

  &:hover:not(.active) {
    background-color: ${(props) => props.theme.colors.greyLightest};
  }
`;

const NovaSideNav = () => {
  const [clickedItem, setClickedItem] = useState({});
  const tagTypesConfig = useWorkspaceTagConfig();
  const { user } = useAuth0();
  const featureFlags = useFeatureFlags();
  const { userTeams } = useUserTeams();
  const { pinnedEntities } = usePinnedEntities();
  const { sideNav, setKeepExpanded } = useUserSideNav();
  const groupTypes = useGroupTypes() || {};
  const location = useLocation();
  const sideNavHeight = useAppHeadersHeightCalc({
    includeProfileHeaders: false,
    hideHeader: false,
  });

  const navItems = useSideNavItemBuilder({
    clickedItem,
    user,
    tagTypesConfig,
    featureFlags,
    eventEmitter,
    setClickedItem,
  });

  useSideNavClickListener();
  const filteredNavItems = useFilterNavItems(navItems);

  const handleKeepExpandedChange = useCallback(
    (keepExpanded) => {
      setKeepExpanded(keepExpanded);
    },
    [setKeepExpanded]
  );

  if (isEmpty(filteredNavItems)) {
    return null;
  }

  return (
    <SideNavStateProvider>
      <SideNavV2
        items={filteredNavItems}
        sideNavHeight={sideNavHeight}
        yourTeams={userTeams}
        pinnedItems={pinnedEntities}
        startExpanded={sideNav?.keepExpanded}
        onKeepExpandedChange={handleKeepExpandedChange}
        renderTeamsSection={({ teams, isExpanded }) => {
          if (!teams || teams.length === 0) {
            return null;
          }

          const renderTeamItem = (team) => {
            const { avatar, name, link, gradient, type, id } = team;
            if (!name || !link) {
              return null;
            }

            const groupObject = { type, id, name };
            const hasTeamType = Boolean(type);
            const groupType = hasTeamType
              ? getGroupType(groupObject, groupTypes)
              : null;
            const badgeVariant = groupType
              ? getGroupTypeBadgeVariant(groupType)
              : null;
            const isActive = link && location.pathname.startsWith(link);

            // Same popover content for both collapsed and expanded states
            const teamPopoverContent = hasTeamType ? (
              <Flex
                flexDirection="row"
                flexWrap="wrap"
                gap="xs"
                alignItems="center"
              >
                <span>{name}</span>
                <GroupTypeBadge
                  group={groupObject}
                  groupTypes={groupTypes}
                  style={{
                    flexShrink: 0,
                    fontSize: "11px",
                    padding: "2px 6px",
                    lineHeight: "1.2",
                  }}
                />
              </Flex>
            ) : (
              <span>{name}</span>
            );

            if (isExpanded) {
              return (
                <Item
                  key={id || name}
                  isExpanded={isExpanded}
                  className={isActive ? "active" : ""}
                >
                  <Popover
                    text={teamPopoverContent}
                    direction="right"
                    width="fit-content"
                  >
                    <TeamLink to={link} aria-label={name}>
                      <Avatar
                        image={avatar}
                        customSize="24px"
                        shape="square"
                        gradient={gradient}
                      />
                      {hasTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                      <span>{name}</span>
                    </TeamLink>
                  </Popover>
                </Item>
              );
            }

            return (
              <Item key={id || name} isExpanded={isExpanded}>
                <Popover
                  text={teamPopoverContent}
                  direction="right"
                  width="fit-content"
                >
                  <CollapsedItemWrapper className={isActive ? "active" : ""}>
                    <Link
                      to={link}
                      aria-label={name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <Avatar
                        image={avatar}
                        customSize="24px"
                        shape="square"
                        gradient={gradient}
                      />
                      {hasTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                    </Link>
                  </CollapsedItemWrapper>
                </Popover>
              </Item>
            );
          };

          return (
            <>
              <Divider light display={["none", "none", "none", "block"]} />
              <Flex
                flexDirection="column"
                gap="r"
                my={isExpanded ? "s" : "0"}
                display={["none", "none", "none", "flex"]}
              >
                {isExpanded && (
                  <H6 sizing="small" color="greyDark">
                    My Teams
                  </H6>
                )}
                <ItemList isExpanded={isExpanded}>
                  {teams.map(renderTeamItem)}
                </ItemList>
              </Flex>
            </>
          );
        }}
        renderPinnedSection={({ items: pinnedItems, isExpanded }) => {
          if (!pinnedItems || pinnedItems.length === 0) {
            return null;
          }

          const renderPinnedItem = (item) => {
            const {
              avatar,
              name,
              link,
              shape,
              onUnpin,
              gradient,
              type,
              entityType,
              id,
              jobTitle,
              tagTypeName,
            } = item;
            if (!name || !link) {
              return null;
            }

            const showTeamType = entityType === "team" && type;
            const groupObject = showTeamType ? { type, id, name } : null;
            const groupType = showTeamType
              ? getGroupType(groupObject, groupTypes)
              : null;
            const badgeVariant = groupType
              ? getGroupTypeBadgeVariant(groupType)
              : null;
            const isActive = link && location.pathname.startsWith(link);

            // Same popover content for both collapsed and expanded states
            const getPopoverContent = () => {
              if (entityType === "team" && showTeamType) {
                return (
                  <Flex
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="xs"
                    alignItems="center"
                  >
                    <span>{name}</span>
                    <GroupTypeBadge
                      group={groupObject}
                      groupTypes={groupTypes}
                      style={{
                        flexShrink: 0,
                        fontSize: "11px",
                        padding: "2px 6px",
                        lineHeight: "1.2",
                      }}
                    />
                  </Flex>
                );
              }

              if (entityType === "person") {
                return (
                  <Flex
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="xs"
                    alignItems="center"
                  >
                    <span>{name}</span>
                    {jobTitle && (
                      <Badge
                        style={{
                          flexShrink: 0,
                          fontSize: "11px",
                          padding: "2px 6px",
                          lineHeight: "1.2",
                        }}
                      >
                        {jobTitle}
                      </Badge>
                    )}
                  </Flex>
                );
              }

              if (entityType === "tag") {
                return (
                  <Flex
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="xs"
                    alignItems="center"
                  >
                    <span>{name}</span>
                    <Badge
                      style={{
                        flexShrink: 0,
                        fontSize: "11px",
                        padding: "2px 6px",
                        lineHeight: "1.2",
                      }}
                    >
                      {tagTypeName}
                    </Badge>
                  </Flex>
                );
              }

              return <span>{name}</span>;
            };

            if (isExpanded) {
              return (
                <Item
                  key={id || name}
                  isExpanded={isExpanded}
                  className={isActive ? "active" : ""}
                >
                  <Popover
                    text={getPopoverContent()}
                    direction="right"
                    width="fit-content"
                  >
                    <TeamLink to={link} aria-label={name}>
                      <Avatar
                        image={avatar}
                        customSize="24px"
                        shape={shape}
                        gradient={gradient}
                      />
                      {showTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                      <span>{name}</span>
                    </TeamLink>
                  </Popover>
                  {onUnpin && (
                    <Popover
                      text="Unpin item"
                      direction="right"
                      width="fit-content"
                      tabIndex="-1"
                    >
                      <Button
                        height="20px"
                        width="20px"
                        borderRadius="20px"
                        variant="borderGrey"
                        borderColor="white"
                        onClick={() => onUnpin(item)}
                      >
                        <Icon
                          icon={["fas", "thumbtack"]}
                          color="greyDarkest"
                          size="sm"
                        />
                      </Button>
                    </Popover>
                  )}
                </Item>
              );
            }

            return (
              <Item key={id || name} isExpanded={isExpanded}>
                <Popover
                  text={getPopoverContent()}
                  direction="right"
                  width="fit-content"
                >
                  <CollapsedItemWrapper className={isActive ? "active" : ""}>
                    <Link
                      to={link}
                      aria-label={name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <Avatar
                        image={avatar}
                        customSize="24px"
                        shape={shape}
                        gradient={gradient}
                      />
                      {showTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                    </Link>
                  </CollapsedItemWrapper>
                </Popover>
              </Item>
            );
          };

          return (
            <>
              <Divider light display={["none", "none", "none", "block"]} />
              <Flex
                flexDirection="column"
                gap="r"
                my={isExpanded ? "s" : "0"}
                display={["none", "none", "none", "flex"]}
              >
                {isExpanded && (
                  <H6 sizing="small" color="greyDark">
                    Pinned
                  </H6>
                )}
                <ItemList isExpanded={isExpanded}>
                  {pinnedItems.map(renderPinnedItem)}
                </ItemList>
              </Flex>
            </>
          );
        }}
      />
    </SideNavStateProvider>
  );
};

export default NovaSideNav;
